/* eslint-disable camelcase */

import uniqBy from 'lodash/uniqBy';

export type CategoryTreeItem = {
  id: string;
  name: string;
  semantic_id: string;
  product_collection: string;
  externalUri: string;
  children: CategoryTreeItem[];
  logo: { 192: string } | string;
  name_hierarchical?: string;
  pathName?: string; // Added to make this more compatible with campaign item
};

type CategoryTree = CategoryTreeItem[];

export const getSubCategoryMap = (
  categories: CategoryTree,
  limitSubCategories: number,
  displayLinkedCategories: boolean
) => {
  const result: Record<string, CategoryTreeItem[]> = categories.reduce(
    (map, { id, children, semantic_id: path }) => {
      if (
        displayLinkedCategories ||
        typeof path !== 'string' ||
        !Array.isArray(children) ||
        !children.length
      ) {
        return {
          ...map,
          [id]: Array.isArray(children)
            ? children.slice(0, limitSubCategories)
            : [],
        };
      }
      // Filter out product collection duplicates
      const uniqueSubCategories = uniqBy(
        children,
        ({ id: subCategoryId, product_collection }) =>
          product_collection || subCategoryId
      );
      /**
       * HACK: Use semantic URL to filter out linked categories
       *
       * Semantic URL will contain a category's true path, so we can use it
       * here to determine if a category is linked (virtual) or in the correct
       * part of the tree.
       *
       * Example:
       * "Memory Cards" is listed under "Phones & GPS" > "Mobile Phone Accessories"
       * (/phones-gps/mobile-phone-accessories--c1307). But from the semantic URL
       * /computers-accessories/storage-media/memory-cards--c156 we can tell that it
       * actually belongs Under "Computers & Accessories" > "Storage Media".
       */
      const pathWithoutId = path.replace(/--c\d+$/, '');
      const pathLength = pathWithoutId.length;
      return {
        ...map,
        [id]: uniqueSubCategories
          .filter(
            ({ semantic_id: subCategoryPath }) =>
              subCategoryPath.substring(0, pathLength) === pathWithoutId
          )
          .slice(0, limitSubCategories),
      };
    },
    {}
  );
  // Special case - categories with only virtual children will become empty
  // In this case we will fall back to display children of linked categories
  if (!displayLinkedCategories) {
    Object.keys(result).forEach(key => {
      const subCategories = result[key];
      const category = categories.find(({ id }) => id === key);
      if (
        !subCategories.length &&
        category &&
        Array.isArray(category.children) &&
        category.children.length
      ) {
        // Create list with all other categories children
        const otherSubCategories = Object.keys(result)
          .filter(k => k !== key)
          .flatMap(k =>
            result[k].map(
              ({ product_collection, id }) => `${product_collection}|${id}`
            )
          );
        const subCategoriesWithLinks = category.children.filter(
          ({ id, product_collection }) =>
            !otherSubCategories.includes(`${product_collection}|${id}`)
        );
        result[key] = subCategoriesWithLinks.length
          ? subCategoriesWithLinks.slice(0, limitSubCategories)
          : category.children.slice(0, limitSubCategories);
      }
    });
  }
  return result;
};
